import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { useSettings } from "@/lib/settings";

const field = "w-full rounded-2xl border border-fg/10 bg-[var(--ink)] px-4 py-3 text-fg outline-none placeholder:text-mist focus:border-[var(--volt)]/60";
const card = "rounded-3xl border border-fg/10 bg-[var(--ink-2)] p-6";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs uppercase tracking-widest text-mist">{label}</span>
    {children}
  </label>
);

const SiteForm = () => {
  const { setSettings } = useSettings();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/admin/settings").then((r) => setF(r.data || {})).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!f) return null;

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.put("/admin/settings", f);
      setSettings((s) => ({ ...s, ...data }));
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const input = (k, props = {}) => <input value={f[k] ?? ""} onChange={set(k)} data-testid={`settings-${k.replace(/_/g, "-")}-input`} className={field} {...props} />;

  return (
    <form onSubmit={save} className="space-y-4" data-testid="settings-form">
      <section className={`${card} space-y-4`}>
        <h3 className="font-display text-xl">Avvisi email</h3>
        <Field label="Email che riceve nuove richieste e ordini pagati">{input("notify_email", { type: "email", required: true })}</Field>
      </section>
      <section className={`${card} grid gap-4 sm:grid-cols-2`}>
        <h3 className="font-display text-xl sm:col-span-2">Contatti del negozio</h3>
        <Field label="Indirizzo">{input("shop_address")}</Field>
        <Field label="Telefono">{input("shop_phone")}</Field>
        <Field label="Email di contatto">{input("shop_email", { type: "email" })}</Field>
        <Field label="P.IVA">{input("shop_vat")}</Field>
      </section>
      <section className={`${card} grid gap-4 sm:grid-cols-3`}>
        <h3 className="font-display text-xl sm:col-span-3">Testi della home</h3>
        <Field label="Titolo riga 1">{input("hero_line1", { required: true, maxLength: 24 })}</Field>
        <Field label="Riga 2 (in evidenza)">{input("hero_line2", { required: true, maxLength: 24 })}</Field>
        <Field label="Riga 3">{input("hero_line3", { required: true, maxLength: 24 })}</Field>
        <div className="sm:col-span-3">
          <Field label="Frase sotto il titolo">
            <textarea rows={3} maxLength={300} value={f.hero_subtitle ?? ""} onChange={set("hero_subtitle")} data-testid="settings-hero-subtitle-input" className={field} />
          </Field>
        </div>
      </section>
      <button disabled={busy} data-testid="settings-save-button" className="flex items-center gap-2 rounded-full bg-[var(--volt)] px-7 py-3.5 font-semibold text-[var(--ink)] disabled:opacity-60 active:scale-95">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva impostazioni
      </button>
    </form>
  );
};

const PasswordForm = () => {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    if (f.next !== f.confirm) { toast.error("Le due password nuove non coincidono"); return; }
    setBusy(true);
    try {
      await http.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      setF({ current: "", next: "", confirm: "" });
      toast.success("Password aggiornata");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className={`${card} grid gap-4 sm:grid-cols-3`} data-testid="password-form">
      <h3 className="font-display text-xl sm:col-span-3">Password admin</h3>
      <Field label="Password attuale"><input type="password" required value={f.current} onChange={set("current")} data-testid="password-current-input" className={field} /></Field>
      <Field label="Nuova (min. 8)"><input type="password" required minLength={8} value={f.next} onChange={set("next")} data-testid="password-new-input" className={field} /></Field>
      <Field label="Ripeti nuova"><input type="password" required minLength={8} value={f.confirm} onChange={set("confirm")} data-testid="password-confirm-input" className={field} /></Field>
      <button disabled={busy} data-testid="password-save-button" className="w-fit rounded-full border border-fg/25 px-6 py-3 text-sm hover:bg-fg/5 disabled:opacity-60 sm:col-span-3">Cambia password</button>
    </form>
  );
};

export const SettingsPanel = () => (
  <div className="mt-4 space-y-4">
    <SiteForm />
    <PasswordForm />
  </div>
);
